/* The one save that matters.
 *
 * Everything in the manager edits a draft: an upload, a caption, a reorder, a
 * deleted album. None of it reaches the client's site until this bar is used —
 * which is the point, because the alternative (0060's) published every
 * intermediate state, including the half-finished ones.
 *
 * It sits at the top of every manager screen rather than at the end of one flow.
 * There is no "last step" here: someone can upload on one screen, reorder on
 * another and rename on a third, and the thing they all have in common is that
 * none of it is live yet.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { cancelGalleryChanges, publishGallery } from '@/data/client';
import { useGalleries } from './useGalleries';

const ago = (iso: string): string => {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  return `${Math.round(hrs / 24)} day${Math.round(hrs / 24) === 1 ? '' : 's'} ago`;
};

export function SaveBar({ onError }: { onError?: (m: string) => void }) {
  const qc = useQueryClient();
  const { pending, pendingSince, publishedAt } = useGalleries();

  /* Said once, then it goes quiet. The resting state already reports "up to
     date, saved 2 minutes ago", but that is the same sentence whether you saved
     just now or yesterday — pressing a button and watching a row change tense is
     not an answer to "did that work". */
  const [justSaved, setJustSaved] = useState<'' | 'saved' | 'building'>('');
  useEffect(() => {
    if (!justSaved) return;
    const t = setTimeout(() => setJustSaved(''), 6000);
    return () => clearTimeout(t);
  }, [justSaved]);

  const save = useMutation({
    mutationFn: publishGallery,
    onSuccess: (r) => {
      setJustSaved(r.rebuilding ? 'building' : 'saved');
      void qc.invalidateQueries({ queryKey: ['galleries'] });
    },
    onError: (e) => onError?.(
      e instanceof Error ? e.message.replace(/^API \d+:\s*/, '') : 'Could not save to your site.',
    ),
  });

  /* Everything, not one screen's worth: the draft is per client, so cancelling
     in an album discards the upload made in another one too. That is why it
     asks, and why the question names what it covers rather than saying "are you
     sure". */
  const cancel = useMutation({
    mutationFn: cancelGalleryChanges,
    onSuccess: () => {
      setJustSaved('');
      // Every gallery read is stale at once — rows, albums, items, the lot.
      void qc.invalidateQueries();
    },
    onError: (e) => onError?.(
      e instanceof Error ? e.message.replace(/^API \d+:\s*/, '') : 'Could not undo those changes.',
    ),
  });

  const busy = save.isPending || cancel.isPending;
  const askThenCancel = () => {
    const since = pendingSince ? ` made since ${ago(pendingSince)}` : '';
    if (!confirm(
      `Discard every gallery change${since}?

`
      + 'Photos you have added will be removed, and anything you renamed, '
      + 'reordered or deleted goes back to how your site has it now. '
      + 'This cannot be undone.',
    )) return;
    cancel.mutate();
  };

  return (
    <div className={`es-savestate${pending ? ' is-pending' : ''}${justSaved ? ' is-saved' : ''}`}>
      <span className="es-savestate__msg">
        <Icon name={pending && !justSaved ? 'alert-circle' : 'check-circle'} size={15} />
        {justSaved === 'building'
          ? <>Saved. Your site is rebuilding &mdash; give it a few minutes.</>
          : justSaved === 'saved'
            ? <>Saved to your site.</>
            : pending
              ? <>Changes since {ago(pendingSince)} aren&rsquo;t on your site yet.</>
              : publishedAt
                ? <>Your site is up to date &mdash; saved {ago(publishedAt)}.</>
                : <>Nothing to save yet.</>}
      </span>
      {pending && (
        <span className="es-savestate__acts">
          <Button variant="ghost" size="sm" disabled={busy} onClick={askThenCancel}>
            {cancel.isPending ? 'Discarding…' : 'Cancel changes'}
          </Button>
          <Button variant="primary" size="sm" disabled={busy}
            iconLeft={<Icon name="upload" size={15} />}
            onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save to site'}
          </Button>
        </span>
      )}
    </div>
  );
}
